import { useTrackResourceView } from "@core/activities/hooks";
import { Resource } from "@core/shared/types";
import { trans } from "@mongez/localization";
import { CheckCircle, Eye, Play, XCircle } from "lucide-react";
import { getResourceAssessmentResult } from "../utils/get-resource-assessment-result";

export function useResourceAssessment(resource: Resource) {
  const assessment = resource.assessment;

  const { trackResourceView } = useTrackResourceView(resource);

  if (!assessment) {
    return null;
  }

  const { studentQuiz } = assessment;

  const status = studentQuiz?.status || "notStarted";

  const duration = assessment.duration || 0;
  const result = getResourceAssessmentResult(studentQuiz);
  const isInReview = status === "inReview";
  const hasFinalScore = result?.scorePercentage !== undefined;
  const resultLabel = result
    ? trans(`assessments.${result.status}`)
    : undefined;
  const resultClassName =
    result?.status === "passed"
      ? "text-green-600 dark:text-green-400"
      : "text-red-600 dark:text-red-400";

  const getButtonConfig = () => {
    const baseConfig = {
      variant: "default" as const,
      onClick: () => trackResourceView(),
    };

    switch (status) {
      case "notStarted":
        return {
          ...baseConfig,
          color: "primary" as const,
          icon: Play,
          text: trans("assessments.startAssessment"),
          className:
            "bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105",
        };

      case "active":
        return {
          ...baseConfig,
          color: "orange" as const,
          icon: Play,
          text: trans("continueAssessment"),
          className:
            "bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105",
        };

      case "passed":
        return {
          ...baseConfig,
          color: "green" as const,
          icon: CheckCircle,
          text: trans("viewResults"),
          className:
            "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105",
        };

      case "failed":
        return {
          ...baseConfig,
          color: "red" as const,
          icon: XCircle,
          text: trans("retakeAssessment"),
          className:
            "bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105",
        };

      case "inReview":
        return {
          ...baseConfig,
          color: "blue" as const,
          icon: Eye,
          text: trans("viewResults"),
          className:
            "bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105",
        };

      default:
        return {
          ...baseConfig,
          color: "primary" as const,
          icon: Play,
          text: trans("openAssessment"),
          className:
            "bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105",
        };
    }
  };

  const buttonConfig = getButtonConfig();

  const IconComponent = buttonConfig.icon;

  return {
    status,
    duration,
    buttonConfig,
    IconComponent,
    trackResourceView,
    assessment,
    studentQuiz,
    result,
    isInReview,
    hasFinalScore,
    resultLabel,
    resultClassName,
  };
}
